import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FilesManagerService } from '../../services/files-manager.service';
import { OverlayService } from '../../services/overlay.service';
import { NewApiResponse } from '../../services/new-api-http.service';
import { finalize } from 'rxjs/operators';
import { MaintenanceService } from '../../services/maintenance.service';

@Component({
	selector: 'app-file-manager',
	templateUrl: './file-manager.component.html',
	styleUrls: ['./file-manager.component.scss']
})
export class FileManagerComponent implements OnInit {
	@Input() allowedDimensions;
	@Input() limitations: any;
	@Input() fileType;
	@Output() output = new EventEmitter();
	viewMode = 'list';
	isPopup: boolean;
	total;
	start;
	currentPage;
	type;
	order;
	fileList: FileObject[] = [];
	openSideBar = false;
	selectedFile: FileObject = {
		name: '',
		last_modified: '',
		path: '',
		type: '',
		size: 0,
		fileType: ''
	};
	filterBy = 'images';
	maxPaginationItems = 10;
	uploading = false;
	listHeight = window.innerHeight * 0.63;
	fileToEdit;
	isModalShown = false;
	loaded = false;
	mainHeight = window.innerHeight - 127;
	importUrl;

	constructor(
		private sanitation: DomSanitizer,
		private route: ActivatedRoute,
		private router: Router,
		private overlay: OverlayService,
		private files: FilesManagerService,
		private cd: ChangeDetectorRef,
		private maintenance: MaintenanceService
	) {}

	ngOnInit() {
		this.isPopup = Boolean(this.fileType);
		this.viewMode = this.route.snapshot.params['view'] ? this.route.snapshot.params['view'] : 'list';
		this.start = this.route.snapshot.params['start'] ? this.route.snapshot.params['start'] : 0;
		this.currentPage = +this.start + 1;

		if (this.route.snapshot.params['type']) {
			this.type = this.route.snapshot.params['type'];
		} else if (this.fileType) {
			this.type = this.fileType;
		} else {
			this.type = '';
		}

		this.order = this.route.snapshot.params['order'] ? this.route.snapshot.params['order'] : 'date';
		this.getFileList(this.start, this.type, this.order);
	}

	getFileList(start, type, order) {
		this.overlay.updateOverlay(true, 'Generating File List...');
		const self = this;
		this.files.getFileList(start, type, order).subscribe(
			(response: NewApiResponse) => {
				this.overlay.updateOverlay(false);
				if (response && response.status) {
					this.total = response.data.total;
					if (typeof response.data.files !== 'undefined') {
						this.fileList = Object.keys(response.data.files).map(function (key) {
							const instance = response.data.files[key];
							const oldPath = response.data.files[key].path;
							const link = self.sanitation.bypassSecurityTrustUrl(response.data.files[key].path);
							const safePath = self.sanitation.bypassSecurityTrustStyle('url(' + response.data.files[key].path + ')');
							instance.path = safePath;
							instance.oldPath = oldPath;
							instance.link = link;

							return instance;
						});
					} else {
						this.fileList = [];
					}
				}
				this.cd.markForCheck();
				this.loaded = true;
			},
			(err) => {
				this.overlay.updateOverlay(true, JSON.stringify(err), 'alert');
				this.maintenance.serverMaintenance(err);
			}
		);
	}

	pageChanged(event: any, isReset: boolean = Boolean(false)): void | boolean {
		window.scrollTo(0, 0);
		this.start = !isNaN(parseFloat(event)) && isFinite(event) ? event : event.page - 1;
		this.currentPage = +this.start + 1;
		const urlParams = ['file-manager', this.viewMode, this.start];

		if (this.order) {
			urlParams.push(this.order);
		}

		if (this.type) {
			urlParams.push(this.type);
		}

		if (!this.fileType) {
			if (!Boolean(event) && isReset) {
				this.router.navigate(['/file-manager']);
			} else {
				this.router.navigate(urlParams).then(() => {
					this.getFileList(this.start, this.type, this.order);
				});
			}
		} else {
			this.getFileList(this.start, this.type, this.order);
		}
	}

	uploadFile(event) {
		const fileList: FileList = event.target.files;
		if (fileList.length === 0) {
			return false;
		}
		this.uploading = true;
		this.overlay.updateOverlay(true, 'Uploading file...');
		let error = false;
		let count = 0;
		for (const key in fileList) {
			if (fileList.length > parseInt(key)) {
				const single: File = fileList[key];
				this.files
					.uploadFile(single)
					.pipe(
						finalize(() => {
							if (fileList.length == count) {
								this.uploading = false;
								if (!error) {
									this.overlay.updateOverlay(false);
									this.pageChanged(0);
								}
							}
						})
					)
					.subscribe(
						(res) => {
							count++;
							if (!res['status']) {
								this.overlay.updateOverlay(true, res['data'], 'alert');
							}
						},
						(err) => {
							error = true;
							count++;
							this.overlay.updateOverlay(true, err, 'alert');
						}
					);
			}
		}
	}

	importFile(importUrl) {
		this.uploading = true;
		this.overlay.updateOverlay(true, 'Importing file...');
		this.files.importFile(importUrl).subscribe((response: any) => {
			if (response && response.status) {
				this.importUrl = '';
				setTimeout(() => {
					this.uploading = false;
					this.overlay.updateOverlay(false);
					this.pageChanged(0);
				}, 2000);
			} else {
				this.overlay.updateOverlay(true, response.message, 'alert');
				this.importUrl = '';
			}
			this.uploading = false;
		});
	}

	deleteFile(fileName) {
		this.uploading = true;

		this.files.deleteFile(fileName).subscribe((response: any) => {
			if (response && response.status) {
				this.pageChanged(0);
			}
			this.uploading = false;
		});
	}

	handleOutput(event) {
		const self = this;
		switch (event.method) {
			case 'delete': {
				this.deleteFile(event.fileName);
				break;
			}

			case 'selected': {
				this.output.emit({ method: 'selected', url: event.fileName });
				break;
			}

			case 'edit': {
				this.fileToEdit = event.file;
				this.isModalShown = true;
				break;
			}

			case 'cancel': {
				this.isModalShown = false;
				break;
			}

			case 'saveClose': {
				const file: File = new File([event.blob], event.fileName, { type: event.blob.type, lastModified: Date.now() });
				this.files.uploadFile(file).subscribe(
					(res) => {
						if (res && res['status']) {
							this.isModalShown = false;
							this.pageChanged(0);
						} else {
							this.overlay.updateOverlay(true, res['data'], 'alert');
						}
					},
					(error) => this.overlay.updateOverlay(true, error, 'alert')
				);
				break;
			}

			case 'saveSelect': {
				const file: File = new File([event.blob], event.fileName, { type: event.blob.type, lastModified: Date.now() });
				this.files.uploadFile(file).subscribe((res) => {
					if (res && res['status']) {
						self.isModalShown = false;
						self.output.emit({ method: 'selected', url: res['data'] });
					} else {
						self.overlay.updateOverlay(true, res['data'], 'alert');
					}
				});
				break;
			}

			default: {
				break;
			}
		}
	}
}

export interface FileObject {
	name: string;
	last_modified: string;
	path: string;
	type: string;
	size: number;
	fileType: string;
}
